import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { updateDocumentDirection } from '@/i18n/config';
import { useMemo } from 'react';

const languages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
];

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  const changeLanguage = (langCode: string) => {
    // Force save to localStorage first
    localStorage.setItem('i18nextLng', langCode);

    // Then change language in i18n
    i18n.changeLanguage(langCode).then(() => {
      updateDocumentDirection(langCode);

      // Force page refresh to ensure all components update
      window.location.reload();
    });
  };

  // Get current language display text
  const currentLanguage = languages.find(lang => lang.code === i18n.language);
  const currentDisplayText = currentLanguage
    ? currentLanguage.nativeName
    : 'English';

  return (
    <DropdownMenu dir={isRTL ? 'ltr' : 'rtl'}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-3 text-sm font-medium"
        >
          {currentDisplayText}
          <span className="sr-only">{t('common.language')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={isRTL ? 'start' : 'end'}
        className="w-48 z-[99999] space-y-2"
        sideOffset={5}
        alignOffset={0}
        side="bottom"
        avoidCollisions={true}
        collisionPadding={16}
        sticky="always"
      >
        {languages.map(language => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => changeLanguage(language.code)}
            className={`flex items-center justify-between ${
              i18n.language === language.code ? 'bg-accent' : ''
            }`}
          >
            <span className="text-sm">{language.name}</span>
            <span className="text-sm  ">{language.nativeName}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
